import { Button, Breadcrumb, Card, Divider, Switch, theme } from 'antd';
import {
  EditOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { VendorDetailResponse } from '@/types/api';
import { TimestampPair } from '@/components/common/TimestampDisplay';
import CategoryTag from '@/components/common/CategoryTag';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { getOrganizationMenuItems } from '@/constants/navigation.constants';
import type { ColorScheme, FontSizes } from './types';

interface VendorHeaderProps {
  vendor: VendorDetailResponse;
  canEdit: boolean;
  toggling: boolean;
  onEditClick: () => void;
  onToggleStatus: (vendor: VendorDetailResponse) => void;
  currentColorScheme: ColorScheme;
  currentFontSizes: FontSizes;
}

export const VendorHeader = ({
  vendor,
  canEdit,
  toggling,
  currentColorScheme,
  currentFontSizes,
  onEditClick,
  onToggleStatus,
}: VendorHeaderProps) => {
  const { t } = useTranslation('vendors');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const elevation = useElevation();

  return (
    <Card
      className="tw-mb-6"
      style={{
        ...elevation.card,
        background: currentColorScheme.headerBg,
      }}
    >
      {/* Back button + Breadcrumb aligned horizontally */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/auth/vendors')}
          style={{
            fontSize: currentFontSizes.body,
            padding: 0,
            height: 'auto',
            lineHeight: 1,
          }}
        >
          {t('vendor.buttons.back')}
        </Button>
        <Breadcrumb
          items={[
            {
              title: t('vendor.breadcrumbs.masterData'),
            },
            {
              title: <a onClick={() => navigate('/auth/vendors')}>{t('vendor.breadcrumbs.organization')}</a>,
              menu: {
                items: getOrganizationMenuItems(navigate, tCommon)?.filter(
                  (item): item is NonNullable<typeof item> => item !== null
                ),
              },
            },
            {
              title: t('vendor.breadcrumbs.vendor'),
            },
            {
              title: vendor.name,
            },
          ]}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Header content */}
      <div className="tw-flex tw-justify-between tw-items-start tw-gap-6 tw-mb-6">
        <div className="tw-flex-1">
          <div className="tw-mb-2">
            <h1
              style={{
                fontSize: currentFontSizes.title,
                fontWeight: token.fontWeightStrong,
                lineHeight: 1.2,
                color: currentColorScheme.headerAccent,
                margin: 0,
              }}
            >
              {vendor.name}
              {vendor.nickname && (
                <span
                  style={{
                    fontSize: token.fontSizeLG,
                    color: token.colorTextTertiary,
                    fontWeight: 'normal',
                    fontStyle: 'italic',
                    marginLeft: '12px',
                  }}
                >
                  ({vendor.nickname})
                </span>
              )}
            </h1>
            {vendor.alternativeName && (
              <p
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorTextTertiary,
                  margin: '4px 0 0 0',
                }}
              >
                {vendor.alternativeName}
              </p>
            )}
          </div>

          {/* Notes */}
          {vendor.notes && (
            <p
              style={{
                fontSize: currentFontSizes.subtitle,
                color: token.colorTextSecondary,
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              {vendor.notes}
            </p>
          )}
        </div>

        <Button
          type="primary"
          icon={<EditOutlined />}
          disabled={!canEdit}
          onClick={onEditClick}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            fontSize: currentFontSizes.body,
          }}
        >
          {t('vendor.actions.edit')}
        </Button>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      <div className="tw-flex tw-gap-8 tw-flex-wrap" style={{ paddingTop: '8px' }}>
        {/* Vendor Code */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('vendor.labels.vendorCodeLabel')}
          </span>
          <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
            {vendor.vendorCode || '-'}
          </span>
        </div>

        {/* Vendor ID */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('vendor.labels.vendorIdLabel')}
          </span>
          <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
            {vendor.vendorId}
          </span>
        </div>

        {/* Vendor Type */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('vendor.labels.typeLabel')}
          </span>
          <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
            {vendor.individual ? t('vendor.vendorTypes.individual') : t('vendor.vendorTypes.company')}
          </span>
        </div>

        {/* Categories */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <TagOutlined
            style={{ fontSize: currentFontSizes.body, color: token.colorTextSecondary }}
          />
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('vendor.labels.categoriesLabel')}
          </span>
          <div className="tw-flex tw-gap-1 tw-flex-wrap">
            {vendor.categories && vendor.categories.length > 0
              ? vendor.categories.map((cat) => (
                  <CategoryTag
                    key={cat.categoryId}
                    name={cat.category}
                    color={cat.color || token.colorPrimary}
                  />
                ))
              : '-'}
          </div>
        </div>

        {/* Timestamps */}
        <TimestampPair
          createdAt={vendor.createdAt}
          updatedAt={vendor.updatedAt}
          direction="horizontal"
        />

        {/* Status with Action Button */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <CheckCircleOutlined
            style={{ fontSize: currentFontSizes.body, color: token.colorTextSecondary }}
          />
          <span
            style={{
              fontSize: currentFontSizes.label,
              color: token.colorTextSecondary,
            }}
          >
            {t('vendor.labels.statusLabel')}
          </span>
          <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
            {vendor.isActive ? t('vendor.status.active') : t('vendor.status.inactive')}
          </span>
          <Switch
            checked={vendor.isActive}
            onChange={() => onToggleStatus(vendor)}
            loading={toggling}
            style={{
              transform: 'scale(0.8)',
            }}
          />
        </div>
      </div>
    </Card>
  );
};

export default VendorHeader;
